// src/utils/webCitations.ts

import type { WebCitationInsertion, WebCitationSource } from "../interfaces/webCitationTypes";

export interface AppliedWebCitations {
  /** The message text with the citation tags spliced in */
  content: string;
  /** Maps a position in the original text to the same place in `content` */
  shift: (position: number) => number;
  /** How many insertions were spliced in; the rest did not match their anchor */
  applied: number;
}

/**
 * Safe inside a double-quoted attribute, and inside a markdown table row: GFM
 * splits a row on every "|", even one inside an HTML attribute, and a line
 * break would end the row.
 */
const escapeAttribute = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\|/g, "&#124;")
    .replace(/[\r\n]+/g, " ");

const citeTags = (sources: WebCitationSource[]): string =>
  sources
    .map((source) => {
      const pageTitle = source.page_title
        ? ` data-title="${escapeAttribute(source.page_title)}"`
        : "";

      return `<cite source="${escapeAttribute(source.title)}" url="${escapeAttribute(source.url)}"${pageTitle} data-grounded="true"/>`;
    })
    .join("");

/**
 * Splice <cite source url/> tags into a finished response, the same tags Claude
 * writes, so MarkdownRenderer renders them as citation chips. They are marked
 * `data-grounded`, which is what keeps the Gemini-only rendering (merged chips,
 * site-first tooltip) away from Claude's own citations.
 *
 * Each insertion is placed only where the text before it matches its anchor.
 * Inline tool events are positioned by offsets into the same text, so callers
 * must move them with `shift`: an event at or after an insertion point moves
 * past the tags, keeping a citation with the sentence before it rather than
 * after a tool card that follows.
 */
export const applyWebCitations = (
  content: string,
  insertions: WebCitationInsertion[]
): AppliedWebCitations => {
  const spliced: { at: number; length: number }[] = [];
  let result = content;

  // Highest position first, so earlier positions stay valid while splicing.
  const ordered = [...insertions].sort((a, b) => b.at - a.at);

  for (const insertion of ordered) {
    const { at, anchor } = insertion;

    if (at > content.length || content.slice(at - anchor.length, at) !== anchor) {
      continue;
    }
    if (spliced.some((done) => done.at === at)) {
      continue;
    }
    const tags = citeTags(insertion.sources);

    result = result.slice(0, at) + tags + result.slice(at);
    spliced.push({ at, length: tags.length });
  }

  const shift = (position: number): number =>
    spliced.reduce((moved, done) => (done.at <= position ? moved + done.length : moved), position);

  return { content: result, shift, applied: spliced.length };
};
